
import { Instagram, Facebook } from 'lucide-react';
import Logo from './Logo';
import { useLocation, useNavigate } from 'react-router-dom';

const INSTAGRAM_URL = 'https://www.instagram.com/czule.klisze/';

const openExternalUrl = (url: string) => {
  try {
    if (window.top && window.top !== window) {
      window.top.location.href = url;
      return;
    }
  } catch {
    window.location.href = url;
    return;
  }

  window.location.href = url;
};

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const navigate = useNavigate();
  
  const handleNavigation = (href: string) => {
    // Jeśli to link wewnątrzstronicowy
    if (href.startsWith('#')) {
      // Sprawdź, czy jesteśmy na stronie głównej
      if (location.pathname !== '/') {
        console.log('Footer: Nawigacja ze strony', location.pathname, 'do sekcji', href);
        // Jeśli nie, nawiguj do strony głównej i przewiń do sekcji
        navigate('/');
        // Po małym opóźnieniu przewiń do sekcji
        setTimeout(() => {
          const element = document.getElementById(href.substring(1));
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          } else {
            console.warn('Footer: Element nie znaleziony:', href.substring(1));
          }
        }, 200);
      } else {
        // Jeśli jesteśmy na stronie głównej, przewiń do sekcji
        const element = document.getElementById(href.substring(1));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        } else {
          console.warn('Footer: Element nie znaleziony na stronie głównej:', href.substring(1));
        }
      }
    } else if (href === '/') {
      // Jeśli to link do strony głównej, przejdź i przewiń na górę
      navigate(href);
      window.scrollTo(0, 0);
    } else {
      // Standardowa nawigacja do innych podstron
      navigate(href);
      window.scrollTo(0, 0);
    }
  };
  
  return (
    <footer className="bg-navy-900 text-white py-6 md:py-16">
      <div className="container-custom">
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-10">
          <div>
            <div className="mb-2 md:mb-4 filter brightness-0 invert scale-90 origin-left">
              <Logo />
            </div>
            <div className="mb-3 md:mb-6">
              <p className="font-display text-sm md:text-base text-white leading-relaxed">
                "Zdjęcia to bilety powrotne do chwil, które minęły"
              </p>
            </div>
            <div className="flex space-x-2 md:space-x-4">
              <a href={INSTAGRAM_URL} target="_top" rel="noopener noreferrer" onClick={(event) => { event.preventDefault(); openExternalUrl(INSTAGRAM_URL); }} className="text-white hover:text-elegant-400 transition-colors" aria-label="Instagram">
                <Instagram size={16} className="md:w-5 md:h-5" />
              </a>
              <a href="https://www.facebook.com/Czuleklisze" target="_blank" rel="noopener noreferrer" className="text-white hover:text-elegant-400 transition-colors" aria-label="Facebook">
                <Facebook size={16} className="md:w-5 md:h-5" />
              </a>
              <a href="https://vimeo.com/czuleklisze" target="_blank" rel="noopener noreferrer" className="text-white hover:text-elegant-400 transition-colors" aria-label="Vimeo">
                <div className="w-4 h-4 md:w-5 md:h-5 relative group">
                  <img 
                    src="/lovable-uploads/509e12fd-628e-4b8c-96b3-6f6c30739c20.png" 
                    alt="Vimeo" 
                    className="w-full h-full filter brightness-0 invert group-hover:opacity-0 transition-opacity absolute inset-0" 
                  />
                  <img 
                    src="/lovable-uploads/509e12fd-628e-4b8c-96b3-6f6c30739c20.png" 
                    alt="Vimeo" 
                    className="w-full h-full filter brightness-0 invert opacity-0 group-hover:opacity-100 transition-opacity absolute inset-0 text-elegant-400" 
                    style={{ filter: "brightness(0) invert(0.7)" }}
                  />
                </div>
              </a>
            </div>
          </div>
          
          <div>
            <h4 className="text-xs md:text-lg font-serif mb-1 md:mb-4">Szybkie Linki</h4>
            <ul className="space-y-0.5 md:space-y-2 text-[10px] md:text-sm">
              {[
                { name: 'Strona Główna', href: '/' },
                { name: 'Galeria', href: '#gallery' },
                { name: 'Proces', href: '#process' },
                { name: 'O Nas', href: '#about' },
                { name: 'FAQ', href: '#faq' },
                { name: 'Blog', href: '/blog' },
                { name: 'Kontakt', href: '#contact' }
              ].map((item) => (
                <li key={item.name}>
                  <button 
                    className="text-[10px] md:text-sm text-white hover:text-elegant-400 transition-colors inline-block border-b border-transparent hover:border-white pb-0 md:pb-1"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavigation(item.href);
                    }}
                  >
                    {item.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h4 className="text-xs md:text-lg font-serif mb-1 md:mb-4">Kontakt</h4>
            <address className="not-italic text-[10px] md:text-sm text-white">
              <p className="mb-1 md:mb-2 text-white">Wrocław, Polska</p>
              <p>
                <a href="mailto:kontakt@czuleklisze.pl" className="text-white hover:text-elegant-400 transition-colors">
                  kontakt@czuleklisze.pl
                </a>
              </p>
            </address>
          </div>
        </div>
        
        <div className="mt-4 md:mt-12 pt-2 md:pt-8 border-t border-navy-800 text-center">
          <div className="flex flex-col md:flex-row justify-center items-center gap-1 md:gap-4 mb-2 md:mb-4 text-[10px] md:text-sm text-white">
            <p>&copy; {currentYear} Czułe klisze. Wszelkie prawa zastrzeżone.</p>
            <span className="hidden md:inline">|</span>
            <button 
              onClick={() => handleNavigation('/polityka-prywatnosci')}
              className="text-white hover:text-elegant-400 transition-colors"
            >
              Polityka Prywatności
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
